// src/hooks/useNotificationApi.tsx

import {
  useState,
  useCallback,
  createContext,
  useContext,
  useEffect,
  ReactNode,
} from 'react'

import { authHeader, notifyError, notifySuccess } from '../api/'
import { usePreloader } from '../components/'
import { languageConstants as LC } from '../constants/'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env

// context

const NotificationCtx = createContext({ data: [] })

type Props = {
  children: ReactNode
}

const UNKNOWN_ERROR = 'Unbekannter Fehler'

export const NotificationDataProvider = ({ children }: Props) => {
  const { getPool } = useNotificationApi()

  const [data, setData] = useState([])

  useEffect(() => {
    ;(async () => {
      try {
        const result = await getPool()
        setData(result)
      } catch (error) {
        const statusCode = error?.status ?? error?.response?.status
        notifyError(`NotificationApi: GET notifications ${statusCode}`)
      }
    })()
  }, [getPool])

  return (
    <NotificationCtx.Provider value={{ data }}>
      {children}
    </NotificationCtx.Provider>
  )
}

export const useNotificationPool = () => useContext(NotificationCtx).data

export const useNotificationApi = () => {
  const { start, stop } = usePreloader()
  const pool = useContext(NotificationCtx)

  const withRequest = useCallback(async fn => {
    try {
      return await fn()
    } catch (error) {
      const status = error?.status ?? error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await refreshToken()
          return await fn()
        } catch (e2) {
          throw {
            message:
              e2?.message || (typeof e2 === 'string' ? e2 : UNKNOWN_ERROR),
            status: e2?.status ?? e2?.response?.status ?? 401,
          }
        }
      }

      throw {
        message:
          error?.message || (typeof error === 'string' ? error : UNKNOWN_ERROR),
        status,
      }
    }
  }, [])

  const withPreloader = useCallback(
    async fn => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const getPool = useCallback(() => {
    if (pool && Array.isArray(pool.data) && pool.data.length > 0)
      return pool.data

    return withPreloader(() =>
      withRequest(async () => {
        await refreshToken()

        const url = `${REACT_APP_API_URL}/notification/admin/notification`
        const options = { method: 'GET', headers: { ...authHeader() } }
        const response = await fetch(url, options)

        if (response.status === 204) return []
        if (!response.ok) {
          const text = await response.text().catch(() => '')
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }

        const payload = await response.json()
        return payload?.data ?? []
      })
    )
  }, [withRequest, pool, withPreloader])

  const save = useCallback(
    data =>
      withRequest(async () => {
        const { email, folderID, percent, sensorDeviceID } = data || {}

        if (!folderID) throw new Error(LC.NOTIFICATION_FOLDER_REQUIRED)

        const record = {
          data: {
            attributes: { sensorDeviceID, folderID, email, percent },
          },
        }

        await refreshToken()

        const url = `${REACT_APP_API_URL}/notification/admin/notification`
        const options = {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify(record),
        }

        const response = await fetch(url, options)
        if (!response.ok) {
          const text = await response.text().catch(() => '')
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }

        const result = await response.json()
        notifySuccess(LC.NOTIFICATION_SAVED)

        return result?.id ?? null
      }),
    [withRequest]
  )

  const remove = useCallback(
    id =>
      withRequest(async () => {
        if (!id) throw new Error(LC.NOTIFICATION_ID_REQUIRED)

        await refreshToken()

        const url = `${REACT_APP_API_URL}/notification/admin/notification/${id}`
        const options = {
          method: 'DELETE',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
        }
        const response = await fetch(url, options)

        if (!response.ok) {
          const text = await response.text().catch(() => '')
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }

        notifySuccess(LC.NOTIFICATION_DELETED)

        return true
      }),
    [withRequest]
  )

  return {
    getPool,
    save,
    remove,
  }
}
